import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Divider } from 'rsuite';
import SearchLauncher from '../components/SearchLauncher';
import helpArticles from './help/helpArticles';

import './HelpPage.css';

export default function HelpPage() {
  const navigate = useNavigate();

  const searchItems = useMemo(
    () =>
      helpArticles.map((article) => ({
        id: article.id,
        title: article.title,
        description: article.description,
        destination: article.category,
        iconClass: article.iconClass,
        // Section headings and step text are searchable too, so a query like
        // "effective date" finds the tutorial that mentions it.
        keywords: [
          ...(article.keywords || []),
          ...(article.sections || []).flatMap((s) => [s.heading, s.body, s.tip, ...(s.steps || [])]),
        ].filter(Boolean),
      })),
    []
  );

  const categories = useMemo(() => {
    const byCategory = new Map();
    for (const article of helpArticles) {
      const key = article.category || 'General';
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(article);
    }
    return Array.from(byCategory.entries());
  }, []);

  return (
    <div className="ppc-page ppc-help">
      <h2 className="ppc-help__title">
        <i className="bi bi-question-circle" aria-hidden="true" />
        <span>Help</span>
      </h2>

      <SearchLauncher
        idPrefix="help-search"
        label="Search help"
        placeholder="Search tutorials: new project, task orders, rates, people..."
        resultsLabel="Help search results"
        emptyText="No tutorials matched your search."
        items={searchItems}
        onSelect={(item) => navigate(`/help/${item.id}`)}
      />

      <Divider />

      {categories.map(([category, articles]) => (
        <section key={category} className="ppc-help__category">
          <h3 className="ppc-help__category-title">{category}</h3>
          <div className="ppc-help__grid">
            {articles.map((article) => (
              <button
                key={article.id}
                type="button"
                className="ppc-help__card"
                onClick={() => navigate(`/help/${article.id}`)}
              >
                <span className="ppc-help__card-icon" aria-hidden="true">
                  <i className={article.iconClass || 'bi bi-journal-text'} />
                </span>
                <span className="ppc-help__card-content">
                  <span className="ppc-help__card-title">{article.title}</span>
                  <span className="ppc-help__card-description">{article.description}</span>
                  {article.video?.key ? (
                    <span className="ppc-help__card-meta">
                      <i className="bi bi-play-circle" aria-hidden="true" />
                      Video + steps
                    </span>
                  ) : null}
                </span>
                <i className="bi bi-arrow-right ppc-help__card-chevron" aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
